import type { IRegistrationStatusResponse } from "@frontend-shared/types";
import { ESocialAuthProvider } from "@frontend-shared/types";
import React, { memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useAppConfig } from "@/api/app-config/useAppConfig";
import { apiClient } from "@/api/services";
import {
  GitHub,
  Google,
  TelegramIcon,
  VkIcon,
  YandexIcon,
} from "@/components/icons";
import { useOnPremise } from "@/hooks/use-on-premise";
import { cn } from "@/lib/utils";

import { ContinueWithDivider } from "./ContinueWithDivider";
import { SocialButton } from "./SocialButton";
import { SsoButton } from "./SsoButton";

interface ISocialButtonsProps {
  className?: string;
  registrationStatus?: IRegistrationStatusResponse;
}

interface ISocialButtonItem {
  key: string;
  provider: ESocialAuthProvider;
  icon: React.ReactNode;
  label: string;
  testId: string;
}

export const SocialButtons = memo(
  ({
    className,
    registrationStatus,
  }: ISocialButtonsProps): React.ReactElement | null => {
    const { t } = useTranslation("auth");
    const { isOnPremise } = useOnPremise();
    const { data: appConfig } = useAppConfig();

    const isSSOVisible = appConfig?.auth.isSsoVisible ?? false;
    // NOTE: NSAI-6323 — the divider reads "or continue with", so it only makes
    // sense above a password form. On an SSO-only deployment SignIn renders no
    // form, and a divider introducing nothing is worse than none.
    const isPasswordAuthDisabled =
      appConfig?.auth.isPasswordAuthDisabled ?? false;

    const handleSocialLogin = useCallback(
      (provider: ESocialAuthProvider): void => {
        globalThis.location.assign(
          `${apiClient.instance.defaults.baseURL}/auth/${provider}`,
        );
      },
      [],
    );

    const socialButtonItems = useMemo((): ISocialButtonItem[] => {
      // NOTE: NSAI-6323 — Require SSO closes the OAuth callbacks too
      // (enforceSsoOnlyForOAuthUser), so on an SSO-only deployment a Google or
      // GitHub button is as dead as the password form. Today the on-premise
      // branch already covers every deployment that can carry the flag; the
      // condition is spelled out anyway so the two cannot drift apart.
      if (isOnPremise || isPasswordAuthDisabled) {
        return [];
      }

      const items: ISocialButtonItem[] = [];

      if (registrationStatus?.githubAuthEnabled) {
        items.push({
          key: "github",
          provider: ESocialAuthProvider.GITHUB,
          icon: <GitHub className="size-6 shrink-0" />,
          label: t("social.github"),
          testId: "social-github-button",
        });
      }

      if (registrationStatus?.googleAuthEnabled) {
        items.push({
          key: "google",
          provider: ESocialAuthProvider.GOOGLE,
          icon: <Google className="size-6 shrink-0" />,
          label: t("social.google"),
          testId: "social-google-button",
        });
      }

      if (registrationStatus?.yandexAuthEnabled) {
        items.push({
          key: "yandex",
          provider: ESocialAuthProvider.YANDEX,
          icon: <YandexIcon className="size-6 shrink-0" />,
          label: t("social.yandex"),
          testId: "social-yandex-button",
        });
      }

      if (registrationStatus?.vkAuthEnabled) {
        items.push({
          key: "vk",
          provider: ESocialAuthProvider.VK,
          icon: <VkIcon className="size-6 shrink-0" />,
          label: t("social.vk"),
          testId: "social-vk-button",
        });
      }

      if (registrationStatus?.telegramAuthEnabled) {
        items.push({
          key: "telegram",
          provider: ESocialAuthProvider.TELEGRAM,
          icon: <TelegramIcon className="size-6 shrink-0" />,
          label: t("social.telegram"),
          testId: "social-telegram-button",
        });
      }

      return items;
    }, [isOnPremise, isPasswordAuthDisabled, t, registrationStatus]);

    if (isOnPremise) {
      if (!isSSOVisible) {
        return null;
      }
      return (
        <>
          <SsoButton isCentered />
          {!isPasswordAuthDisabled && <ContinueWithDivider />}
        </>
      );
    }

    if (!isSSOVisible && socialButtonItems.length === 0) {
      return null;
    }

    // NOTE: +1 for SSO button when visible
    const totalButtonCount = socialButtonItems.length + (isSSOVisible ? 1 : 0);

    // NOTE: Center button text when fewer than 3 auth options for better visual balance.
    const shouldCenterText = totalButtonCount < 3;

    // NOTE: SSO is alone on its row when total button count is odd
    const isSsoAloneOnRow = totalButtonCount % 2 === 1;

    return (
      <>
        <div
          className={cn("grid grid-cols-1 gap-4 sm:grid-cols-2", className)}
          data-testid="social-buttons"
        >
          {socialButtonItems.map((item) => (
            <div key={item.key} className="w-full">
              <SocialButton
                onClick={() => handleSocialLogin(item.provider)}
                icon={item.icon}
                label={item.label}
                testId={item.testId}
                isCentered={shouldCenterText}
              />
            </div>
          ))}
          {isSSOVisible && (
            <div
              className={cn("w-full", isSsoAloneOnRow ? "sm:col-span-2" : null)}
            >
              <SsoButton isCentered={shouldCenterText || isSsoAloneOnRow} />
            </div>
          )}
        </div>
        {!isPasswordAuthDisabled && <ContinueWithDivider />}
      </>
    );
  },
);
